import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "fourier-series";

const DEFAULT_BG = "#0a0e1f";
const DEFAULT_CIRCLE = "#475569";
const DEFAULT_VECTOR = "#22d3ee";
const DEFAULT_TRAIL = "#fde68a";

// We approximate a square wave with its first NUM_HARMONICS odd
// harmonics: f(t) = (4/π) Σ sin((2n−1)·ωt) / (2n−1). Each harmonic is
// rendered as one rotating circle ("epicycle") whose radius equals the
// magnitude of its coefficient. Circles are chained tip-to-center so the
// final tip traces the partial sum — the same construction used in
// classic visualizations of the Fourier series.
const NUM_HARMONICS = 7;
const ANGULAR_SPEED = 0.00065;     // base ω in rad/ms — the whole composition
                                   // completes one cycle in ~9.6 s
const TRAIL_LIFESPAN_MS = 8000;    // how long a sample stays visible in the trail
const SAMPLE_INTERVAL_MS = 16;     // resample at ~60Hz; trail uses real time
                                   // for x mapping, so frame-rate hiccups don't
                                   // bunch the wave together
const MAX_TRAIL_SAMPLES = 600;

export default function FourierSeries({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([
    DEFAULT_BG,
    DEFAULT_CIRCLE,
    DEFAULT_VECTOR,
    DEFAULT_TRAIL,
  ]);
  const trailRef = useRef([]); // [{ y, t }] — y is canvas-space, t is ms since start
  const startTsRef = useRef(null);
  const lastSampleTsRef = useRef(0);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_CIRCLE),
      pickColor(colors, 2, DEFAULT_VECTOR),
      pickColor(colors, 3, DEFAULT_TRAIL),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Layout changed; old trail samples reference stale y coords, so
      // dropping them avoids a jagged jump in the wave.
      trailRef.current = [];
    };

    const tick = (ts) => {
      if (startTsRef.current === null) startTsRef.current = ts;
      const t = ts - startTsRef.current;
      const [bg, circleCol, vectorCol, trailCol] = colorsRef.current;

      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      // Layout: epicycles live in the left ~30% of the canvas; the wave
      // trail unfurls to the right. baseR is sized so the largest circle
      // stays well inside that region even on tall portrait tiles.
      const cx = width * 0.30;
      const cy = height / 2;
      const baseR = Math.min(width * 0.20, height * 0.34);

      // March through harmonics, accumulating a chain of points. n runs
      // 1, 3, 5, ... so we hit the odd-only square-wave spectrum. Each
      // circle has radius (4/π)·baseR / n and angular speed n·ω.
      const points = [{ x: cx, y: cy }];
      for (let i = 0; i < NUM_HARMONICS; i += 1) {
        const n = 2 * i + 1;
        const radius = (baseR * 4) / (Math.PI * n);
        const angle = n * t * ANGULAR_SPEED;
        const prev = points[points.length - 1];
        points.push({
          x: prev.x + radius * Math.cos(angle),
          y: prev.y + radius * Math.sin(angle),
        });
      }
      const tip = points[points.length - 1];

      // Sample the trail at a fixed interval rather than every frame.
      // Pairing each sample with its real timestamp lets us map age → x
      // for a constant horizontal drift that's independent of FPS.
      if (t - lastSampleTsRef.current >= SAMPLE_INTERVAL_MS) {
        trailRef.current.push({ y: tip.y, t });
        lastSampleTsRef.current = t;
        if (trailRef.current.length > MAX_TRAIL_SAMPLES) {
          trailRef.current.splice(0, trailRef.current.length - MAX_TRAIL_SAMPLES);
        }
      }

      // Drop trail samples older than the lifespan so memory stays bounded
      // even after long uptime.
      while (
        trailRef.current.length &&
        t - trailRef.current[0].t > TRAIL_LIFESPAN_MS
      ) {
        trailRef.current.shift();
      }

      const trailStartX = cx + baseR * 1.45;
      const trailEndX = width - 6;
      const trailWidth = Math.max(1, trailEndX - trailStartX);

      // Faint guide circles for each epicycle. Drawn first so vectors
      // and the trail sit on top.
      ctx.strokeStyle = rgba(circleCol, 0.18);
      ctx.lineWidth = 1;
      for (let i = 0; i < NUM_HARMONICS; i += 1) {
        const n = 2 * i + 1;
        const radius = (baseR * 4) / (Math.PI * n);
        ctx.beginPath();
        ctx.arc(points[i].x, points[i].y, radius, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Chain of vector arrows from one circle's center to the next.
      ctx.strokeStyle = rgba(vectorCol, 0.85);
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i += 1) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.stroke();

      // Small dots at each junction so the vector chain is clearly
      // articulated.
      ctx.fillStyle = rgba(vectorCol, 0.95);
      for (let i = 1; i < points.length; i += 1) {
        ctx.beginPath();
        ctx.arc(points[i].x, points[i].y, 2, 0, Math.PI * 2);
        ctx.fill();
      }

      // Connector from the tip to the trail's left edge — visually
      // explains "this is the value being plotted".
      ctx.strokeStyle = rgba(trailCol, 0.45);
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(tip.x, tip.y);
      ctx.lineTo(trailStartX, tip.y);
      ctx.stroke();
      ctx.setLineDash([]);

      // The trail itself. Newest sample sits at trailStartX, older
      // samples drift right based on (t - sample.t).
      const trail = trailRef.current;
      if (trail.length > 1) {
        ctx.strokeStyle = rgba(trailCol, 0.85);
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        for (let i = trail.length - 1; i >= 0; i -= 1) {
          const sample = trail[i];
          const age = t - sample.t;
          const px = trailStartX + (age / TRAIL_LIFESPAN_MS) * trailWidth;
          if (i === trail.length - 1) ctx.moveTo(px, sample.y);
          else ctx.lineTo(px, sample.y);
        }
        ctx.stroke();
      }

      // Bright dot at the tip's projection on the trail.
      const tipR = 3.2;
      const halo = ctx.createRadialGradient(trailStartX, tip.y, 0, trailStartX, tip.y, tipR * 4);
      halo.addColorStop(0, rgba(trailCol, 0.7));
      halo.addColorStop(1, rgba(trailCol, 0));
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(trailStartX, tip.y, tipR * 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = rgba(trailCol, 1);
      ctx.beginPath();
      ctx.arc(trailStartX, tip.y, tipR, 0, Math.PI * 2);
      ctx.fill();

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
